import { useState, useMemo, useEffect, useRef } from "react"
import { Link, useNavigate } from "@tanstack/react-router"
import {
  Sparkles,
  ArrowRight,
  ArrowUpRight,
  CheckCircle2,
  ShieldCheck,
  Building2,
  MousePointer2,
  Layers,
  ChevronDown,
  Check,
  MapPin,
  Clock,
} from "lucide-react"
import { Button } from "@/components/ui/button"
import { motion } from "framer-motion"
import { TransparentTrendingIcon } from "@/components/dashboard/buyer/components/TransparentTrendingIcon"
import { cn } from "@/lib/utils"
import { useAuthStore } from "@/store/auth.store"

// ── Interactive Category Scenarios ──
interface SupplierInfo {
  name: string
  location: string
  capacity: string
  badge: string
  auditScore: string
  licenseId: string
  onTimeRate: string
  auditor: string
}

interface SampleItem {
  title: string
  specs: string
  price: string
  unit: string
  moq: string
  certBadge: string
  leadTime: string
  supplier: SupplierInfo
}

interface ScenarioItem {
  category: string
  label: string
  icon: string
  rfqId: string
  keyword: string
  productTitle: string
  buyerPrompt: string
  aiResponse: string
  sample1: SampleItem
  sample2: SampleItem
}

const SCENARIOS: ScenarioItem[] = [
  {
    category: "Organic Honey",
    label: "Organic Honey",
    icon: "/trending/honey-3d.jpg",
    rfqId: "RFQ-3321",
    keyword: "organic wild forest honey",
    productTitle: "Wild Forest Honey / NMR Tested",
    buyerPrompt: "Need 4,000 kg Pure Organic Wild Forest Honey in food-grade drums with EU residue lab analysis.",
    aiResponse: "Matched audited apiary cooperative with NMR purity certification & export customs clearance:",
    sample1: {
      title: "Raw Multiflora Forest Honey",
      specs: "Moisture < 18% • Raw Unfiltered",
      price: "$6.20",
      unit: "/ kg [FOB]",
      moq: "1,000 kg",
      certBadge: "NMR 100% Pure",
      leadTime: "⚡ 3-5d Dispatch",
      supplier: {
        name: "Himalayan Nectar Exports",
        location: "Dehradun Hub, Uttarakhand",
        capacity: "25 MT / month",
        badge: "EU Certified",
        auditScore: "Match 97% • NMR Verified",
        licenseId: "IND-DDN-3321-EXP",
        onTimeRate: "99.2%",
        auditor: "Intertek",
      },
    },
    sample2: {
      title: "Single-Flora Acacia Honey",
      specs: "Light Amber • HMF < 15 mg/kg",
      price: "$7.10",
      unit: "/ kg [FOB]",
      moq: "1,500 kg",
      certBadge: "USDA Organic",
      leadTime: "⚡ 7d Dispatch",
      supplier: {
        name: "Kashmir Valley Apiaries",
        location: "Anantnag, J&K",
        capacity: "18 MT / month",
        badge: "Export Grade",
        auditScore: "Match 94% • High-Brix",
        licenseId: "IND-SXR-1902-AGR",
        onTimeRate: "98.5%",
        auditor: "SGS Certified",
      },
    },
  },
  {
    category: "Fresh Mango",
    label: "Fresh Mango",
    icon: "/trending/mango-3d.jpg",
    rfqId: "RFQ-4190",
    keyword: "export grade fresh mangoes",
    productTitle: "Alphonso & Kesar Fresh Mango / Global GAP",
    buyerPrompt: "Need 12,000 kg Export-Grade Alphonso Mangoes CIF Rotterdam with GlobalG.A.P certification & hot water dip treatment.",
    aiResponse: "2 certified packing houses matched with verified cold-chain logistics & phytosanitary clearance:",
    sample1: {
      title: "Grade-A Alphonso (Ratnagiri Origin)",
      specs: "220-250g • Hot Water Dip Treated • Brix > 18°",
      price: "$3.80",
      unit: "/ kg [CIF]",
      moq: "3,000 kg",
      certBadge: "GlobalG.A.P",
      leadTime: "⚡ 48h Cold-Chain",
      supplier: {
        name: "Ratnagiri Agro Export FPO",
        location: "Ratnagiri Belt, Maharashtra",
        capacity: "80 MT / season",
        badge: "APEDA Verified",
        auditScore: "Match 97% • Cold-Chain Tracked",
        licenseId: "IND-RTN-4190-APD",
        onTimeRate: "99.5%",
        auditor: "SGS India",
      },
    },
    sample2: {
      title: "Organic Kesar Fresh Mango",
      specs: "100% Residue Free • Uniform Yellow • 200g+",
      price: "$3.20",
      unit: "/ kg [FOB]",
      moq: "5,000 kg",
      certBadge: "USDA Organic",
      leadTime: "⚡ 3d Reefer Loading",
      supplier: {
        name: "Gir Organic Fruit Growers",
        location: "Junagadh Cluster, Gujarat",
        capacity: "120 MT / season",
        badge: "Eco Producer",
        auditScore: "Match 95% • Residue Free",
        licenseId: "IND-JND-8114-ORG",
        onTimeRate: "98.9%",
        auditor: "Control Union",
      },
    },
  },
  {
    category: "Cotton Yarn",
    label: "Cotton Yarn",
    icon: "/trending/yarn-3d.jpg",
    rfqId: "RFQ-8402",
    keyword: "combed cotton yarn (30s)",
    productTitle: "Combed Cotton Yarn (30s) / Audit Passed",
    buyerPrompt: "Need 20,000 kg Combed Cotton Yarn (30s) CIF Hamburg Port with certified OEKO-TEX compliance.",
    aiResponse: "2 verified mills matched your MOQ & fiber specs. Select either sample to inspect verified mill profile:",
    sample1: {
      title: "Ring Spun Combed 30/1",
      specs: "Moisture < 6% • Premium Staple",
      price: "$4.65",
      unit: "/ kg [FOB]",
      moq: "5,000 kg",
      certBadge: "OEKO-TEX 100",
      leadTime: "⚡ 5-7d Dispatch",
      supplier: {
        name: "Vardhman Mills Ltd",
        location: "Surat Hub, Gujarat",
        capacity: "120 MT / month",
        badge: "Tier-1 Partner",
        auditScore: "Match 98% • Tier-1 Verified",
        licenseId: "IND-SUR-8402-KYC",
        onTimeRate: "99.4%",
        auditor: "SGS Audited",
      },
    },
    sample2: {
      title: "Carded Organic Cotton 24s",
      specs: "GOTS Certified • 100% Cotton Fiber",
      price: "$4.15",
      unit: "/ kg [FOB]",
      moq: "10,000 kg",
      certBadge: "GOTS Organic",
      leadTime: "⚡ 10d Dispatch",
      supplier: {
        name: "Aura Spinners Co.",
        location: "Coimbatore, Tamil Nadu",
        capacity: "85 MT / month",
        badge: "Eco Partner",
        auditScore: "Match 95% • Certified Mill",
        licenseId: "IND-CBT-7714-GOTS",
        onTimeRate: "98.8%",
        auditor: "Bureau Veritas",
      },
    },
  },
  {
    category: "Spices",
    label: "Spices",
    icon: "/trending/spices-3d.jpg",
    rfqId: "RFQ-7294",
    keyword: "spices & black pepper",
    productTitle: "Malabar Tellicherry Garbled Extra Bold",
    buyerPrompt: "Need 10,000 kg TGSEB Black Pepper, bulk jute sacks, shipment to Rotterdam Port next month.",
    aiResponse: "Matched spice board registered exporter with ETO sterilization & fumigation certificates:",
    sample1: {
      title: "Tellicherry TGSEB Black Pepper",
      specs: "Bulk Density 580 g/L • Moisture 11%",
      price: "$7.80",
      unit: "/ kg",
      moq: "2,000 kg",
      certBadge: "ETO Sterilized",
      leadTime: "⚡ Port Ready",
      supplier: {
        name: "Malabar Spice Consortium",
        location: "Kochi Port Cluster, Kerala",
        capacity: "200 MT / month",
        badge: "Spice Board India",
        auditScore: "Match 96% • Spices Board",
        licenseId: "IND-KCH-7294-FUM",
        onTimeRate: "99.5%",
        auditor: "Eurofins Lab",
      },
    },
    sample2: {
      title: "Aleppey Finger Turmeric (5.2% Curcumin)",
      specs: "5.2% Curcumin • Polished Fingers",
      price: "$3.40",
      unit: "/ kg [FOB]",
      moq: "5,000 kg",
      certBadge: "Non-GMO Verified",
      leadTime: "⚡ 5d Dispatch",
      supplier: {
        name: "Deccan Agritech Exporters",
        location: "Erode Spice Yard, Tamil Nadu",
        capacity: "350 MT / month",
        badge: "APEDA Certified",
        auditScore: "Match 95% • High-Curcumin",
        licenseId: "IND-ERD-4102-AP",
        onTimeRate: "99.0%",
        auditor: "SGS Food Lab",
      },
    },
  },
  {
    category: "Coffee Beans",
    label: "Coffee Beans",
    icon: "/trending/coffee-3d.jpg",
    rfqId: "RFQ-6088",
    keyword: "specialty arabica green coffee beans",
    productTitle: "Plantation A Arabica / Rainforest Alliance",
    buyerPrompt: "Need 18,000 kg Single-Origin Plantation A Arabica Green Coffee Beans Screen 17+ with Rainforest Alliance audit.",
    aiResponse: "2 certified estate curing mills matched with SCA grading 84+ & export ocean container loading:",
    sample1: {
      title: "Mysore Plantation A Arabica (Washed)",
      specs: "Screen 17 • SCA 84.5 • Moisture < 11.5%",
      price: "$5.40",
      unit: "/ kg [FOB]",
      moq: "5,000 kg",
      certBadge: "Rainforest Alliance",
      leadTime: "⚡ 5-7d Port Dispatch",
      supplier: {
        name: "Chikmagalur Highlands Estates",
        location: "Chikmagalur, Karnataka",
        capacity: "150 MT / harvest",
        badge: "Specialty Coffee Assn",
        auditScore: "Match 99% • Estate Audited",
        licenseId: "IND-CKM-6088-CFI",
        onTimeRate: "99.7%",
        auditor: "Q-Grader Certified",
      },
    },
    sample2: {
      title: "Coorg Robust Peaberry Specialty",
      specs: "High Body • Uniform Density • Clean Cup",
      price: "$4.20",
      unit: "/ kg [FOB]",
      moq: "6,000 kg",
      certBadge: "Fair Trade",
      leadTime: "⚡ 10d Dispatch",
      supplier: {
        name: "Kodagu Valley Growers Consortium",
        location: "Madikeri, Karnataka",
        capacity: "200 MT / harvest",
        badge: "Fair Trade Certified",
        auditScore: "Match 94% • High Yield",
        licenseId: "IND-MDK-9201-FTC",
        onTimeRate: "98.6%",
        auditor: "FLOCERT Audited",
      },
    },
  },
]

// ── Minimal Lightweight SVG Sparkline Component ──
function Sparkline({ points, isUp }: { points: number[]; isUp: boolean }) {
  const min = Math.min(...points)
  const max = Math.max(...points)
  const range = max - min || 1
  const width = 48
  const height = 18

  const pathData = useMemo(() => {
    return points
      .map((val, i) => {
        const x = (i / (points.length - 1)) * width
        const y = height - ((val - min) / range) * (height - 4) - 2
        return `${i === 0 ? "M" : "L"} ${x.toFixed(1)},${y.toFixed(1)}`
      })
      .join(" ")
  }, [points, min, range])

  return (
    <svg width={width} height={height} className="overflow-visible" aria-hidden="true">
      <path
        d={pathData}
        fill="none"
        stroke={isUp ? "#10B981" : "#EF4444"}
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

// ── Animated Loading Ellipsis Component (. -> .. -> ...) ──
function LoadingDots({ className = "" }: { className?: string }) {
  const [dots, setDots] = useState(".")

  useEffect(() => {
    const timer = setInterval(() => {
      setDots((prev) => {
        if (prev === ".") return ".."
        if (prev === "..") return "..."
        return "."
      })
    }, 400)
    return () => clearInterval(timer)
  }, [])

  return (
    <span className={`inline-block min-w-[14px] text-left text-slate-400 font-normal ${className}`}>
      {dots}
    </span>
  )
}

// ── Interactive Sample Selection Card Component ──
function InteractiveSampleCard({
  sample,
  isSelected,
  onSelect,
}: {
  sample: SampleItem
  isSelected: boolean
  onSelect: () => void
}) {
  return (
    <div
      onClick={onSelect}
      className={`w-full rounded-xl p-2.5 sm:p-3 border transition-all cursor-pointer flex items-center justify-between gap-3 ${
        isSelected
          ? "bg-white/95 border-primary shadow-[inset_0_1px_1.5px_rgba(255,255,255,1),0_4px_12px_-2px_color-mix(in_srgb,var(--primary)_12%,transparent)] ring-1.5 ring-primary/25"
          : "bg-white/75 border-white/90 shadow-[inset_0_1px_1px_rgba(255,255,255,0.9),0_2px_6px_rgba(0,0,0,0.02)] hover:border-primary/40 hover:bg-white/90"
      }`}
    >
      {/* Left: Spec info */}
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <div className="flex flex-col min-w-0">
          <div className="flex items-center gap-2">
            <h4 className="text-xs sm:text-[13px] font-bold text-slate-900 truncate">
              {sample.title}
            </h4>
          </div>
        </div>
      </div>

      {/* Right: Price & Selection Pill */}
      <div className="flex items-center gap-3 shrink-0">
        <div className="text-right">
          <span className="text-xs sm:text-[13px] font-mono font-bold text-slate-900 block leading-tight">
            {sample.price}{" "}
            <span className="text-[9px] font-normal text-slate-500">{sample.unit}</span>
          </span>
        </div>
        <span
          className={`font-bold px-2 py-1 rounded-full text-[10px] flex items-center gap-1 transition-all ${
            isSelected
              ? "bg-primary text-primary-foreground shadow-xs shadow-primary/25"
              : "bg-slate-100 text-slate-600 hover:bg-slate-200"
          }`}
        >
          {isSelected ? "✓ Active" : "Compare"}
        </span>
      </div>
    </div>
  )
}

export function Hero({ className }: { className?: string } = {}) {
  const navigate = useNavigate()
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const [activeScenarioIdx, setActiveScenarioIdx] = useState(0)
  const [selectedSample, setSelectedSample] = useState<1 | 2>(1)
  const [phase, setPhase] = useState<'typing' | 'analyzing' | 'matched'>('matched')
  const [displayedText, setDisplayedText] = useState(SCENARIOS[0].buyerPrompt)
  const [reasoningStep, setReasoningStep] = useState<0 | 1 | 2 | 3>(3)
  const [isReasoningExpanded, setIsReasoningExpanded] = useState<boolean>(true)
  const [complianceStep, setComplianceStep] = useState<0 | 1 | 2 | 3>(3)
  const [supplierReady, setSupplierReady] = useState<boolean>(true)
  const [simulationId, setSimulationId] = useState<number>(0)

  const scenario = SCENARIOS[activeScenarioIdx]
  const activeSample = selectedSample === 1 ? scenario.sample1 : scenario.sample2
  const activeSupplier = activeSample.supplier

  // Typewriter effect for live query (relaxed, human-readable speed)
  useEffect(() => {
    if (phase !== 'typing') return
    setDisplayedText("")
    setReasoningStep(0)
    setIsReasoningExpanded(true)
    setComplianceStep(0)
    setSupplierReady(false)
    let charIdx = 0
    const fullText = scenario.buyerPrompt
    const interval = setInterval(() => {
      setDisplayedText(fullText.slice(0, charIdx + 1))
      charIdx++
      if (charIdx >= fullText.length) {
        clearInterval(interval)
        const timeout = setTimeout(() => {
          setPhase('analyzing')
          setReasoningStep(1)
          setIsReasoningExpanded(true)
        }, 700)
        return () => clearTimeout(timeout)
      }
    }, 36)

    return () => clearInterval(interval)
  }, [phase, activeScenarioIdx, scenario.buyerPrompt, simulationId])

  // Multi-step reasoning sequence during analyzing phase (paced for easy reading)
  useEffect(() => {
    if (phase !== 'analyzing') return

    // Step 1 starts at 0ms (setReasoningStep(1))
    // Step 2 appears after 1100ms
    const step2Timer = setTimeout(() => {
      setReasoningStep(2)
    }, 1100)

    // Step 3 appears after 2200ms
    const step3Timer = setTimeout(() => {
      setReasoningStep(3)
    }, 2200)

    // Processing completed after 3300ms: transition to matched & keep expanded
    const finishTimer = setTimeout(() => {
      setPhase('matched')
      setIsReasoningExpanded(true)
    }, 3300)

    return () => {
      clearTimeout(step2Timer)
      clearTimeout(step3Timer)
      clearTimeout(finishTimer)
    }
  }, [phase])

  // Sequential reveal: Supplier Profile details, then Compliance list items (relaxed rendering cadence)
  useEffect(() => {
    if (phase !== 'matched') {
      setSupplierReady(false)
      setComplianceStep(0)
      return
    }

    // 1. Interactive cards render at t=0
    // 2. Verified Supplier Profile details reveal at t=400ms
    const tSupplier = setTimeout(() => {
      setSupplierReady(true)
    }, 400)

    // 3. Compliance Telemetry list items render sequentially after supplier profile
    const t1 = setTimeout(() => {
      setComplianceStep(1)
    }, 800)

    const t2 = setTimeout(() => {
      setComplianceStep(2)
    }, 1200)

    const t3 = setTimeout(() => {
      setComplianceStep(3)
    }, 1600)

    return () => {
      clearTimeout(tSupplier)
      clearTimeout(t1)
      clearTimeout(t2)
      clearTimeout(t3)
    }
  }, [phase])

  // Auto-rotate Live AI Simulation Control one by one continuously every 3.2s (ignores hover)
  useEffect(() => {
    if (phase !== 'matched') return
    const timeout = setTimeout(() => {
      setActiveScenarioIdx((prev) => (prev + 1) % SCENARIOS.length)
      setSelectedSample(1)
      setDisplayedText("")
      setReasoningStep(0)
      setIsReasoningExpanded(true)
      setComplianceStep(0)
      setSupplierReady(false)
      setPhase('typing')
      setSimulationId((prev) => prev + 1)
    }, 10000)
    return () => clearTimeout(timeout)
  }, [phase, activeScenarioIdx, simulationId])

  const handleScenarioSelect = (idx: number) => {
    setActiveScenarioIdx(idx)
    setSelectedSample(1)
    setDisplayedText("")
    setReasoningStep(0)
    setIsReasoningExpanded(true)
    setComplianceStep(0)
    setSupplierReady(false)
    setPhase('typing')
    setSimulationId((prev) => prev + 1)
  }

  const handleScrollTo = (id: string) => {
    const el = document.querySelector(id)
    if (el) {
      el.scrollIntoView({ behavior: "smooth" })
    }
  }

  return (
    <section
      id="hero"
      className={cn(
        "relative overflow-hidden bg-white text-slate-900 pb-20 md:pb-24 select-none",
        className
      )}
    >
      {/* ── 1. AMBIENT AURA MESH GLOWS (Trustio / Modern Minimalist SaaS Aesthetic) ── */}
      <div className="pointer-events-none absolute inset-0 z-0 overflow-hidden" aria-hidden="true">
        {/* Top Atmosphere Glow behind Transparent Navbar */}
        <div className="absolute -top-28 left-1/2 -translate-x-1/2 w-[840px] h-[300px] rounded-full bg-gradient-to-b from-sky-100/60 via-amber-50/40 to-transparent blur-[90px]" />

        {/* Center-Left Soft Cyan/Sky Aura Glow */}
        <div className="absolute top-1/2 -translate-y-1/2 -left-24 w-[620px] h-[620px] rounded-full bg-sky-200/90 blur-[130px]" />

        {/* Right Amber/Warm Sun Glow */}
        <div className="absolute top-1/4 -right-20 w-[580px] h-[580px] rounded-full bg-amber-100/90 blur-[140px]" />

        {/* Center Subtle Crimson Accent Glow behind Hero Headline */}
        <div className="absolute top-16 left-1/2 -translate-x-1/2 w-[760px] h-[360px] rounded-full bg-red-100/30 blur-[120px]" />

      </div>


      {/* ── 3. Hero Headline & Command Center ── */}
      <div className="relative z-10 min-h-[calc(100vh-clamp(145px,22vh,210px))] min-h-[calc(100dvh-clamp(145px,22dvh,210px))] pt-12 sm:pt-14 flex flex-col justify-center items-center">
        <div className="w-full max-w-6xl mx-auto px-4 sm:px-8 md:px-14 lg:px-8 pt-12 pb-14 sm:pt-14 sm:pb-14 md:py-6 lg:py-4 text-center relative my-auto">

        {/* ── Floating 3D Commodity Assets (Using TransparentTrendingIcon) ── */}

        {/* 1. Top-Left: Arabica Coffee Beans (Commodities / Beverages) */}
        <motion.div
          animate={{ x: [-3, 3, -3], y: [-4, 4, -4], rotate: [8.5, 11.5, 8.5] }}
          transition={{ duration: 5.5, repeat: Infinity, ease: "easeInOut" }}
          whileHover={{ scale: 1.15, rotate: 0, x: 0, y: 0 }}
          onClick={() => handleScenarioSelect(4)}
          className={`flex flex-col items-center absolute left-2 sm:left-4 md:left-2 lg:left-2 xl:-left-6 top-1 sm:top-2 md:top-1 lg:top-3 z-20 cursor-pointer pointer-events-auto group select-none transition-all duration-300 ${
            activeScenarioIdx === 4 ? "scale-105 drop-shadow-[0_0_14px_color-mix(in_srgb,var(--primary)_35%,transparent)]" : "opacity-90 hover:opacity-100"
          }`}
        >
          <div className="w-14 h-14 sm:w-16 sm:h-16 md:w-18 md:h-18 lg:w-24 lg:h-24 xl:w-28 xl:h-28 relative transition-transform duration-300">
            <TransparentTrendingIcon
              src="/trending/coffee-3d.jpg"
              alt="Arabica Coffee Beans"
              className="w-full h-full object-contain filter drop-shadow-[0_12px_24px_rgba(0,0,0,0.11)] transition-all duration-300 group-hover:drop-shadow-[0_18px_32px_rgba(0,0,0,0.18)]"
            />
          </div>
        </motion.div>

        {/* 2. Top-Right: Indian Whole Spices (Spices / Exports) */}
        <motion.div
          animate={{ x: [3, -3, 3], y: [4, -4, 4], rotate: [-12.5, -9.5, -12.5] }}
          transition={{ duration: 5.0, repeat: Infinity, ease: "easeInOut" }}
          whileHover={{ scale: 1.15, rotate: 0, x: 0, y: 0 }}
          onClick={() => handleScenarioSelect(3)}
          className={`flex flex-col items-center absolute right-2 sm:right-4 md:right-2 lg:right-2 xl:-right-6 top-1 sm:top-2 md:top-0 lg:top-2 xl:top-2 z-20 cursor-pointer pointer-events-auto group select-none transition-all duration-300 ${
            activeScenarioIdx === 3 ? "scale-105 drop-shadow-[0_0_14px_color-mix(in_srgb,var(--primary)_35%,transparent)]" : "opacity-90 hover:opacity-100"
          }`}
        >
          <div className="w-14 h-14 sm:w-16 sm:h-16 md:w-18 md:h-18 lg:w-24 lg:h-24 xl:w-28 xl:h-28 relative transition-transform duration-300">
            <TransparentTrendingIcon
              src="/trending/spices-3d.jpg"
              alt="Export Whole Spices"
              className="w-full h-full object-contain filter drop-shadow-[0_12px_24px_rgba(0,0,0,0.11)] transition-all duration-300 group-hover:drop-shadow-[0_18px_32px_rgba(0,0,0,0.18)]"
            />
          </div>
        </motion.div>

        {/* 3. Lower-Left: Cotton Yarn & Polo (Textiles / Apparel) */}
        <motion.div
          animate={{ x: [-3.5, 3.5, -3.5], y: [-4, 3, -4], rotate: [-9.5, -6.5, -9.5] }}
          transition={{ duration: 4.8, repeat: Infinity, ease: "easeInOut" }}
          whileHover={{ scale: 1.15, rotate: 0, x: 0, y: 0 }}
          onClick={() => handleScenarioSelect(2)}
          className={`flex flex-col items-center absolute left-2 sm:left-4 md:left-3 lg:left-14 xl:left-20 -bottom-5 sm:-bottom-3 md:bottom-2 lg:bottom-auto lg:top-40 xl:top-44 z-20 cursor-pointer pointer-events-auto group select-none transition-all duration-300 ${
            activeScenarioIdx === 2 ? "scale-105 drop-shadow-[0_0_14px_color-mix(in_srgb,var(--primary)_35%,transparent)]" : "opacity-90 hover:opacity-100"
          }`}
        >
          <div className="w-14 h-14 sm:w-16 sm:h-16 md:w-18 md:h-18 lg:w-24 lg:h-24 xl:w-28 xl:h-28 relative transition-transform duration-300">
            <TransparentTrendingIcon
              src="/trending/yarn-3d.jpg"
              alt="Cotton Yarn & Apparel"
              className="w-full h-full object-contain filter drop-shadow-[0_12px_24px_rgba(0,0,0,0.11)] transition-all duration-300 group-hover:drop-shadow-[0_18px_32px_rgba(0,0,0,0.18)]"
            />
          </div>
        </motion.div>

        {/* 4. Lower-Right: Artisanal Honey Jar (Agro / Organic) */}
        <motion.div
          animate={{ x: [3.5, -3.5, 3.5], y: [3, -4, 3], rotate: [7.5, 10.5, 7.5] }}
          transition={{ duration: 5.8, repeat: Infinity, ease: "easeInOut" }}
          whileHover={{ scale: 1.15, rotate: 0, x: 0, y: 0 }}
          onClick={() => handleScenarioSelect(0)}
          className={`flex flex-col items-center absolute right-2 sm:right-4 md:right-3 lg:right-14 xl:right-20 -bottom-5 sm:-bottom-3 md:bottom-2 lg:bottom-auto lg:top-40 xl:top-44 z-20 cursor-pointer pointer-events-auto group select-none transition-all duration-300 ${
            activeScenarioIdx === 0 ? "scale-105 drop-shadow-[0_0_14px_color-mix(in_srgb,var(--primary)_35%,transparent)]" : "opacity-90 hover:opacity-100"
          }`}
        >
          <div className="w-14 h-14 sm:w-16 sm:h-16 md:w-18 md:h-18 lg:w-24 lg:h-24 xl:w-28 xl:h-28 relative transition-transform duration-300">
            <TransparentTrendingIcon
              src="/trending/honey-3d.jpg"
              alt="Artisanal Honey Jar"
              className="w-full h-full object-contain filter drop-shadow-[0_12px_24px_rgba(0,0,0,0.11)] transition-all duration-300 group-hover:drop-shadow-[0_18px_32px_rgba(0,0,0,0.18)]"
            />
          </div>
        </motion.div>

        {/* Main Dual-Tone Display Headline */}
        <h1 className="font-sans text-[26px] sm:text-3xl md:text-[38px] lg:text-[46px] xl:text-5xl font-bold tracking-tight leading-[1.18] sm:leading-[1.14] text-slate-950 max-w-4xl mx-auto">
          <span className="block">
            <span className="inline-block whitespace-nowrap">Source anything.</span>{" "}
            <span className="inline-block whitespace-nowrap text-primary font-bold">From anywhere.</span>
          </span>
          <span className="block text-slate-950 mt-1.5 sm:mt-2">
            Stay in control.
          </span>
        </h1>

        {/* Concise Subtitle Value Proposition */}
        <p className="text-xs sm:text-[13px] md:text-sm text-slate-600 dark:text-slate-400 max-w-md sm:max-w-lg mx-auto mt-3 sm:mt-3.5 leading-relaxed font-normal px-2 sm:px-0">
          Describe what you need in plain language. Enga finds verified suppliers, manages RFQ, and handles everything until delivery.
        </p>

        {/* Primary CTA Button Group */}
        <div className="flex flex-col min-[480px]:flex-row items-center justify-center gap-2.5 sm:gap-3 mt-4 sm:mt-5.5 relative z-20 mx-auto">
          <Button
            type="button"
            onClick={() => navigate({ to: isAuthenticated ? "/submit-rfq" : "/signup" })}
            className="relative text-[13px] sm:text-sm font-bold rounded-full h-11 p-1 ps-5.5 pe-12 group transition-all duration-500 hover:ps-12 hover:pe-5.5 w-auto overflow-hidden cursor-pointer bg-primary text-primary-foreground hover:bg-primary-hover shadow-md shadow-primary/25 hover:shadow-lg hover:shadow-primary/30 active:scale-95"
          >
            <span className="relative z-10 transition-all duration-500 whitespace-nowrap">
              Request Quote
            </span>
            <div className="absolute right-1 w-9 h-9 bg-white text-slate-900 rounded-full flex items-center justify-center transition-all duration-500 group-hover:right-[calc(100%-40px)] group-hover:rotate-45 shadow-xs">
              <ArrowUpRight size={15} />
            </div>
          </Button>

          <Button
            type="button"
            variant="outline"
            onClick={() => navigate({ to: "/explore" })}
            className="w-auto rounded-full bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 px-5.5 sm:px-6 h-11 text-[13px] sm:text-sm font-semibold shadow-2xs transition-all active:scale-95 cursor-pointer justify-center whitespace-nowrap"
          >
            Start Explore
          </Button>
        </div>
        </div>
      </div>

      {/* ── 4. The Product Mockup Window (Calibrated Native macOS Desktop App Viewport - 16:10 Ratio) ── */}
      <div className="w-full max-w-5xl xl:max-w-[clamp(1024px,78vw,1380px)] mx-auto px-4 sm:px-6 relative z-20 mt-0">
        
        {/* Native macOS Desktop App Window Container (16:10 Aspect Ratio, Floating Viewport) */}
        <div 
          className="rounded-[20px] sm:rounded-[24px] overflow-hidden bg-white/95 dark:bg-slate-900/95 transition-all duration-300 relative border border-white/20 dark:border-white/10 backdrop-blur-xl"
          style={{
            boxShadow: "0 -8px 24px -6px rgba(0, 0, 0, 0.12), 0 32px 75px -15px rgba(0, 0, 0, 0.32), 0 16px 36px -8px rgba(0, 0, 0, 0.18), 0 4px 12px -3px rgba(0, 0, 0, 0.12), 0 0 16px 1px rgba(255, 255, 255, 0.70), inset 0 0 18px 2px rgba(255, 255, 255, 0.35)"
          }}
        >
          {/* Top Glass Specular Reflection Angle Sheen */}
          <div 
            className="pointer-events-none absolute inset-x-0 top-0 h-28 z-10 select-none"
            style={{
              background: "linear-gradient(180deg, rgba(255, 255, 255, 0.6) 0%, rgba(255, 255, 255, 0.1) 45%, transparent 100%)"
            }}
          />

          {/* Dedicated macOS Window Title Bar (40px) with Slate Chrome Background */}
          <div 
            className="h-8 sm:h-9 w-full px-4 sm:px-5 flex items-center justify-between border-b border-slate-200/90 dark:border-slate-800 bg-[#edf1f6] dark:bg-slate-800/95 select-none shrink-0 relative z-20 backdrop-blur-md"
          >
              {/* Left: Traffic Light Controls */}
              <div className="flex items-center gap-2">
                <span 
                  className="w-3 h-3 rounded-full inline-block cursor-pointer transition-transform active:scale-90"
                  style={{
                    backgroundColor: "#FF5F56",
                    border: "1px solid rgba(0, 0, 0, 0.14)",
                    boxShadow: "inset 0 1px 1px rgba(255, 255, 255, 0.75), inset 0 -0.5px 1px rgba(0, 0, 0, 0.2), 0 1px 2px rgba(0, 0, 0, 0.1)"
                  }}
                />
                <span 
                  className="w-3 h-3 rounded-full inline-block cursor-pointer transition-transform active:scale-90"
                  style={{
                    backgroundColor: "#FFBD2E",
                    border: "1px solid rgba(0, 0, 0, 0.14)",
                    boxShadow: "inset 0 1px 1px rgba(255, 255, 255, 0.75), inset 0 -0.5px 1px rgba(0, 0, 0, 0.2), 0 1px 2px rgba(0, 0, 0, 0.1)"
                  }}
                />
                <span 
                  className="w-3 h-3 rounded-full inline-block cursor-pointer transition-transform active:scale-90"
                  style={{
                    backgroundColor: "#27C93F",
                    border: "1px solid rgba(0, 0, 0, 0.14)",
                    boxShadow: "inset 0 1px 1px rgba(255, 255, 255, 0.75), inset 0 -0.5px 1px rgba(0, 0, 0, 0.2), 0 1px 2px rgba(0, 0, 0, 0.1)"
                  }}
                />
              </div>

              {/* Center: Native macOS Address/Breadcrumb Pill */}
              <div 
                className="px-3 sm:px-3.5 py-0.5 rounded-md sm:rounded-lg text-[10.5px] sm:text-[11px] font-medium text-slate-700 dark:text-slate-200 flex items-center gap-1.5 transition-all bg-black/[0.04] dark:bg-white/[0.08] border border-black/[0.06] dark:border-white/[0.12] shadow-2xs"
              >
                <svg className="w-3 h-3 text-slate-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
                <span>enga</span>
                <span className="text-slate-300 dark:text-slate-600">/</span>
                <span>rfq</span>
                <span className="text-slate-300 dark:text-slate-600">/</span>
                <span className="text-primary font-mono font-bold">{scenario.rfqId}</span>
              </div>
            </div>

          {/* Internal Desktop Split Viewport Grid (16:10 Proportions) */}
          <div className="grid grid-cols-1 md:grid-cols-12 min-h-[580px] lg:min-h-[610px] divide-y md:divide-y-0 md:divide-x divide-white/70 relative z-20">
            {/* ── Left Interactive Activity Feed (7 Columns) ── */}
            <div 
              className="md:col-span-7 p-4 sm:p-5 flex flex-col gap-3.5"
              style={{
                background: "rgba(255, 255, 255, 0.62)",
                backdropFilter: "blur(16px)"
              }}
            >
              {/* Input Query Box (Full-width, rendering text from left to right) */}
              <div 
                className="w-full rounded-xl p-3.5 sm:p-4 min-h-[58px] flex items-center transition-all duration-300 bg-primary/[0.035] border border-primary/[0.22]"
                style={{
                  background: "color-mix(in srgb, var(--primary) 3.5%, transparent)",
                  border: "1px solid color-mix(in srgb, var(--primary) 22%, transparent)",
                  boxShadow: "inset 0 1px 1px rgba(255, 255, 255, 0.9), 0 2px 6px rgba(15, 23, 42, 0.03)"
                }}
              >
                <p className="text-slate-900 text-xs sm:text-sm italic font-medium leading-relaxed text-left w-full">
                  {displayedText}
                  {phase === 'typing' && (
                    <span className="inline-block w-1.5 h-3.5 ml-1 bg-primary animate-pulse align-middle" />
                  )}
                </p>
              </div>

              {/* ── AI Sourcing Reasoning Pipeline & Cards Container ── */}
              <div className="w-full flex flex-col gap-2.5 transition-all duration-300">
                {/* When in typing phase: subtle listening standby */}
                {phase === 'typing' && (
                  <div 
                    className="w-full rounded-xl p-3 text-xs sm:text-sm text-slate-600 italic flex items-center gap-2"
                    style={{
                      background: "rgba(255, 255, 255, 0.6)",
                      border: "1px solid rgba(255, 255, 255, 0.85)",
                      boxShadow: "inset 0 1px 1px rgba(255, 255, 255, 0.9)"
                    }}
                  >
                    <span className="w-2 h-2 rounded-full bg-slate-400 animate-pulse" />
                    <span>Listening for procurement requirements...</span>
                  </div>
                )}

                {/* In 'analyzing' phase (active stepping) OR 'matched' with dropdown expanded/collapsed */}
                {(phase === 'analyzing' || phase === 'matched') && (
                  <div 
                    className="w-full rounded-xl p-3 sm:p-3.5 transition-all duration-300"
                    style={{
                      background: "rgba(255, 255, 255, 0.78)",
                      border: "1px solid rgba(255, 255, 255, 0.95)",
                      boxShadow: "inset 0 1.5px 1px rgba(255, 255, 255, 1), 0 3px 10px -2px rgba(15, 23, 42, 0.05)"
                    }}
                  >
                    {/* Collapsed Dropdown Header (Left: AI Strategy, Right: Verified Steps + Chevron) */}
                    <button
                      type="button"
                      onClick={() => {
                        if (phase === 'matched') {
                          setIsReasoningExpanded((prev) => !prev)
                        }
                      }}
                      className={`w-full flex items-center justify-between text-xs font-semibold text-slate-800 transition-colors ${
                        phase === 'matched' ? 'cursor-pointer hover:text-slate-950 group' : 'cursor-default'
                      }`}
                    >
                      {/* Left: Icon + Title */}
                      <div className="flex items-center gap-2">
                        <div className={`w-4.5 h-4.5 rounded-full flex items-center justify-center text-[10px] ${
                          phase === 'analyzing' ? 'bg-primary/10 text-primary' : 'bg-emerald-500/15 text-emerald-600'
                        }`}>
                          {phase === 'analyzing' ? (
                            <span className="w-2 h-2 rounded-full bg-primary animate-ping" />
                          ) : (
                            <Check className="w-3 h-3 stroke-[3]" />
                          )}
                        </div>
                        <span className="font-bold text-slate-900">
                          {phase === 'analyzing' ? 'Reasoning & Sourcing Strategy' : 'AI Reasoning Strategy'}
                        </span>
                      </div>

                      {/* Right: Steps Status + Dropdown Chevron */}
                      <div className="flex items-center gap-2">
                        <span className="text-[11px] font-normal text-slate-400">
                          {phase === 'analyzing' ? `Step ${reasoningStep} of 3` : '3 steps verified'}
                        </span>
                        {phase === 'analyzing' ? (
                          <span className="text-[10px] font-mono text-primary bg-primary/10 px-2 py-0.5 rounded-full font-semibold animate-pulse">
                            Analyzing...
                          </span>
                        ) : (
                          <ChevronDown
                            className={`w-3.5 h-3.5 text-slate-400 group-hover:text-slate-600 transition-transform duration-200 ${
                              isReasoningExpanded ? 'rotate-180' : ''
                            }`}
                          />
                        )}
                      </div>
                    </button>

                    {/* Expanded Reasoning Step List (with branded check circles & connector lines) */}
                    {(isReasoningExpanded || phase === 'analyzing') && (
                      <div className="mt-3.5 pt-3.5 border-t border-slate-200/70 flex flex-col transition-all duration-300">
                        {/* Step 1: Understanding requirements */}
                        <div className="flex items-start gap-3 sm:gap-3.5 relative pb-4">
                          <div className="w-4.5 h-4.5 rounded-full bg-primary text-primary-foreground flex items-center justify-center shrink-0 mt-0.5 shadow-2xs">
                            <Check className="w-2.5 h-2.5 stroke-[3]" />
                          </div>
                          <div className="flex-1">
                            <h5 className="text-xs sm:text-[13px] font-semibold text-slate-900 leading-tight">
                              Understanding your requirements
                            </h5>
                            <p className="text-[11px] sm:text-xs text-slate-500 mt-1.5 leading-relaxed">
                              You're looking for{" "}
                              <span className="bg-primary/10 text-primary font-semibold px-1.5 py-0.5 rounded text-[11px] border border-primary/20 inline-block">
                                {scenario.keyword}
                              </span>.
                            </p>
                          </div>
                          {/* Connector Line to Step 2 */}
                          <div className="absolute left-[9px] top-6 bottom-0 w-[1.5px] bg-slate-200" />
                        </div>

                        {/* Step 2: Reasoning for search strategy */}
                        {reasoningStep >= 2 && (
                          <div className="flex items-start gap-3 sm:gap-3.5 relative pb-4 animate-in fade-in duration-300">
                            <div className="w-4.5 h-4.5 rounded-full bg-primary text-primary-foreground flex items-center justify-center shrink-0 mt-0.5 shadow-2xs">
                              <Check className="w-2.5 h-2.5 stroke-[3]" />
                            </div>
                            <div className="flex-1">
                              <h5 className="text-xs sm:text-[13px] font-semibold text-slate-900 leading-tight">
                                Reasoning for search strategy
                              </h5>
                              <p className="text-[11px] sm:text-xs text-slate-500 mt-1.5 leading-relaxed">
                                I am thoroughly analyzing product listings, verifying images, descriptions, specs, and supplier credentials.
                              </p>
                            </div>
                            {/* Connector Line to Step 3 */}
                            <div className="absolute left-[9px] top-6 bottom-0 w-[1.5px] bg-slate-200" />
                          </div>
                        )}

                        {/* Step 3: Matching products and search keywords */}
                        {reasoningStep >= 3 && (
                          <div className="flex items-start gap-3 sm:gap-3.5 relative pt-0.5 animate-in fade-in duration-300">
                            <div className="w-4.5 h-4.5 rounded-full bg-primary text-primary-foreground flex items-center justify-center shrink-0 mt-0.5 shadow-2xs">
                              <Check className="w-2.5 h-2.5 stroke-[3]" />
                            </div>
                            <div className="flex-1">
                              <h5 className="text-xs sm:text-[13px] font-semibold text-slate-900 leading-tight">
                                Matching products and search keywords
                              </h5>
                              <p className="text-[11px] sm:text-xs text-slate-500 mt-1.5 leading-relaxed">
                                Matched 2 verified export manufacturers with active production capacity & ISO audits.
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* 2 Interactive Selection Cards (Full-Width Horizontal Stacked Rows) */}
                {phase === 'matched' && (
                  <div className="flex flex-col gap-2 pt-0.5 animate-in fade-in slide-in-from-bottom-2 duration-300">
                    {/* Supplier Matches Heading */}
                    <div className="flex items-center justify-between px-1 pt-1">
                      <div className="flex items-center gap-1.5">
                        <Building2 className="w-3.5 h-3.5 text-primary" />
                        <h4 className="text-xs sm:text-[13px] font-bold text-slate-900">
                          Matched Verified Suppliers
                        </h4>
                      </div>
                    </div>

                    {/* Sample 1 Card */}
                    <InteractiveSampleCard
                      sample={scenario.sample1}
                      isSelected={selectedSample === 1}
                      onSelect={() => setSelectedSample(1)}
                    />

                    {/* Sample 2 Card */}
                    <InteractiveSampleCard
                      sample={scenario.sample2}
                      isSelected={selectedSample === 2}
                      onSelect={() => setSelectedSample(2)}
                    />

                    {/* Micro Guarantee Strip */}
                    <div 
                      className="flex items-center justify-between p-3 rounded-xl text-[11px] text-slate-600 mt-0.5"
                      style={{
                        background: "rgba(255, 255, 255, 0.72)",
                        border: "1px solid rgba(255, 255, 255, 0.9)",
                        boxShadow: "inset 0 1px 1px rgba(255, 255, 255, 0.95), 0 1px 3px rgba(0, 0, 0, 0.02)"
                      }}
                    >
                      <span className="flex items-center gap-1.5 font-medium">
                        <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
                        <span>Direct Mill Contract &middot; 100% Protected</span>
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* ── Right Sourcing Telemetry Rail (5 Columns) ── */}
            <div 
              className="md:col-span-5 p-4 sm:p-5 flex flex-col justify-between gap-3.5"
              style={{
                background: "linear-gradient(180deg, rgba(248, 250, 252, 0.65) 0%, rgba(241, 245, 249, 0.5) 100%)",
                backdropFilter: "blur(16px)",
                borderLeft: "1px solid rgba(255, 255, 255, 0.75)"
              }}
            >
              <div className="flex flex-col gap-3">
                {/* Verified Supplier Profile (Enlarged with rich B2B details) */}
                <div 
                  className="p-4.5 sm:p-5 rounded-2xl border transition-all duration-300"
                  style={{
                    background: supplierReady ? "rgba(255, 255, 255, 0.85)" : "rgba(255, 255, 255, 0.65)",
                    borderColor: supplierReady ? "rgba(255, 255, 255, 0.95)" : "rgba(255, 255, 255, 0.8)",
                    boxShadow: supplierReady
                      ? "inset 0 1.5px 1.5px rgba(255, 255, 255, 1), 0 8px 24px -4px rgba(15, 23, 42, 0.09), 0 2px 6px rgba(15, 23, 42, 0.04)"
                      : "none",
                    opacity: supplierReady ? 1 : 0.82
                  }}
                >
                  {/* Top: Icon + Factory Name + Verified Badge */}
                  <div className="flex items-start gap-3">
                    <div className="relative shrink-0">
                      <div className={`w-11 h-11 rounded-xl flex items-center justify-center shadow-xs transition-colors duration-300 ${
                        supplierReady ? 'bg-primary text-primary-foreground shadow-sm shadow-primary/25' : 'bg-slate-200 text-slate-400'
                      }`}>
                        <Building2 className="w-5 h-5" />
                      </div>
                    </div>
                    <div className="flex flex-col min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-1.5">
                        <span className="text-[13px] sm:text-sm font-bold text-slate-900 truncate">
                          {supplierReady ? activeSupplier.name : 'Evaluating verified suppliers...'}
                        </span>
                      </div>
                      <div className="flex items-center justify-between gap-1 mt-1">
                        <span className="text-[11px] text-slate-500 truncate flex items-center gap-1">
                          <MapPin className="w-3 h-3 text-slate-400 shrink-0" />
                          <span>{supplierReady ? activeSupplier.location : 'Corridor standby'}</span>
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* B2B Supplier Profile Details (2x2 Grid) */}
                  <div className="grid grid-cols-2 gap-2 mt-3 pt-3 border-t border-slate-200/50 text-[11px]">
                    {/* Company Type */}
                    <div 
                      className="p-2.5 rounded-xl flex flex-col justify-between"
                      style={{
                        background: "rgba(255, 255, 255, 0.65)",
                        border: "1px solid rgba(255, 255, 255, 0.85)",
                        boxShadow: "inset 0 1px 1px rgba(255, 255, 255, 0.9)"
                      }}
                    >
                      <span className="text-slate-400 text-[9.5px] uppercase font-semibold tracking-wider">Company Type</span>
                      <span className="font-semibold text-slate-900 text-xs mt-1">
                        {supplierReady ? 'Private Limited' : <span className="text-slate-400 font-normal inline-flex items-center">Awaiting<LoadingDots /></span>}
                      </span>
                    </div>

                    {/* Annual Revenue */}
                    <div 
                      className="p-2.5 rounded-xl flex flex-col justify-between"
                      style={{
                        background: "rgba(255, 255, 255, 0.65)",
                        border: "1px solid rgba(255, 255, 255, 0.85)",
                        boxShadow: "inset 0 1px 1px rgba(255, 255, 255, 0.9)"
                      }}
                    >
                      <span className="text-slate-400 text-[9.5px] uppercase font-semibold tracking-wider">Annual Revenue</span>
                      <span className="font-mono font-bold text-slate-900 text-xs mt-1">
                        {supplierReady ? '5M-10M USD' : <span className="text-slate-400 font-normal font-sans inline-flex items-center">Awaiting<LoadingDots /></span>}
                      </span>
                    </div>

                    {/* Minimum Order Qty (MOQ) */}
                    <div 
                      className="p-2.5 rounded-xl flex flex-col justify-between"
                      style={{
                        background: "rgba(255, 255, 255, 0.65)",
                        border: "1px solid rgba(255, 255, 255, 0.85)",
                        boxShadow: "inset 0 1px 1px rgba(255, 255, 255, 0.9)"
                      }}
                    >
                      <span className="text-slate-400 text-[9.5px] uppercase font-semibold tracking-wider">Minimum Order Qty (MOQ)</span>
                      <span className="font-mono font-bold text-slate-900 text-xs mt-1">
                        {supplierReady ? '150 Units' : <span className="text-slate-400 font-normal font-sans inline-flex items-center">Awaiting<LoadingDots /></span>}
                      </span>
                    </div>

                    {/* Production Capacity */}
                    <div 
                      className="p-2.5 rounded-xl flex flex-col justify-between"
                      style={{
                        background: "rgba(255, 255, 255, 0.65)",
                        border: "1px solid rgba(255, 255, 255, 0.85)",
                        boxShadow: "inset 0 1px 1px rgba(255, 255, 255, 0.9)"
                      }}
                    >
                      <span className="text-slate-400 text-[9.5px] uppercase font-semibold tracking-wider">Production Capacity</span>
                      <span className="font-mono font-bold text-emerald-700 text-xs mt-1">
                        {supplierReady ? '54000 units/month' : <span className="text-slate-400 font-normal font-sans inline-flex items-center">Awaiting<LoadingDots /></span>}
                      </span>
                    </div>
                  </div>

                  {/* Footer telemetry details */}
                  <div className="flex items-center justify-between pt-2.5 mt-2.5 border-t border-slate-200/50 text-[10.5px]">
                    <span className="text-slate-500">
                      Auditor: <span className="font-semibold text-slate-800">{supplierReady ? activeSupplier.auditor : <span className="text-slate-400 font-normal inline-flex items-center">Awaiting<LoadingDots /></span>}</span>
                    </span>
                    <span className="font-semibold text-emerald-700 flex items-center gap-1">
                      <ShieldCheck className="w-3 h-3 text-emerald-600" />
                      <span>TradeSafe™ Escrow Protected</span>
                    </span>
                  </div>
                </div>

                {/* Status Check Card with Emerald Checks */}
                <div 
                  className="rounded-xl p-4 sm:p-4.5 space-y-2.5"
                  style={{
                    background: "rgba(236, 253, 245, 0.75)",
                    border: "1px solid rgba(167, 243, 208, 0.7)",
                    boxShadow: "inset 0 1px 1.5px rgba(255, 255, 255, 0.95), 0 2px 8px -2px rgba(16, 185, 129, 0.06)"
                  }}
                >
                  <div className="text-[10px] sm:text-[11px] font-bold text-emerald-800 uppercase tracking-wider flex items-center justify-between pb-2 border-b border-emerald-200/50">
                    <div className="flex items-center gap-1.5">
                      <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
                      <span>Compliance Telemetry</span>
                    </div>
                  </div>

                  <ul className="space-y-2.5 sm:space-y-3 text-[11px] sm:text-xs text-slate-700 min-h-[82px] flex flex-col justify-start pt-0.5">
                    {complianceStep >= 1 && (
                      <li className="flex items-center gap-2.5 animate-in fade-in slide-in-from-left-2 duration-300">
                        <Check className="w-3.5 h-3.5 text-emerald-600 stroke-[2.5] shrink-0" />
                        <span>Factory KYC & Machine Audit Passed</span>
                      </li>
                    )}
                    {complianceStep >= 2 && (
                      <li className="flex items-center gap-2.5 animate-in fade-in slide-in-from-left-2 duration-300">
                        <Check className="w-3.5 h-3.5 text-emerald-600 stroke-[2.5] shrink-0" />
                        <span>ISO 9001:2015 Machinery Tested</span>
                      </li>
                    )}
                    {complianceStep >= 3 && (
                      <li className="flex items-center gap-2.5 animate-in fade-in slide-in-from-left-2 duration-300">
                        <Check className="w-3.5 h-3.5 text-emerald-600 stroke-[2.5] shrink-0" />
                        <span>Encrypted Anonymous Routing Active</span>
                      </li>
                    )}
                    {complianceStep === 0 && (
                      <li className="text-[11px] text-slate-400 italic flex items-start gap-1.5 py-1">
                        <span>Awaiting verification</span>
                      </li>
                    )}
                  </ul>
                </div>
              </div>
              

              {/* Direct Action Link */}
              <Link
                to={isAuthenticated ? "/submit-rfq" : "/signup"}
                className="w-full py-2.5 rounded-xl bg-primary hover:bg-primary-hover text-primary-foreground font-bold text-xs shadow-sm shadow-primary/25 transition-all flex items-center justify-center gap-1.5 active:scale-95 group/link mt-2 cursor-pointer"
              >
                <span>Unlock details & submit RFQ</span>
                <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover/link:translate-x-0.5" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}